import app, { db } from '../firebase';
import { collection, query, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GiftItem } from '../types/gift';

// Gift admin callables live in europe-west1 (setGlobalOptions). Client default is us-central1.
const giftFunctions = getFunctions(app, 'europe-west1');

// getSettings + selectRandomGifts use Firestore only — no callable, no us-central1/CORS/payload errors.

export interface GiftSettings {
  enabled: boolean;
  minGifts: number;
  maxGifts: number;
  showPopup: boolean;
  autoAccept: boolean;
  // Modal customization
  modalBackgroundImage?: string;
  modalBackgroundColor?: string;
  modalButtonColor?: string;
  modalButtonTextColor?: string;
  modalHeading?: string;
  modalTagline?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface GiftSelectionResult {
  gifts: GiftItem[];
  shouldShowPopup: boolean;
  autoAccept: boolean;
}

class GiftService {
  private giftsCollection = 'gifts';
  private settingsCollection = 'giftSettings';

  // Get all gifts
  async getGifts(activeOnly: boolean = false): Promise<GiftItem[]> {
    try {
      const giftsRef = collection(db, this.giftsCollection);
      let q = query(giftsRef, orderBy('createdAt', 'desc'));
      
      if (activeOnly) {
        const { where } = await import('firebase/firestore');
        q = query(giftsRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      const gifts: GiftItem[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        gifts.push({
          id: doc.id,
          name: data.name || '',
          imageURL: data.imageURL || '',
          description: data.description || '',
          rarity: data.rarity || 'common'
        } as GiftItem);
      });

      return gifts;
    } catch (error) {
      console.error('Error fetching gifts:', error);
      throw new Error('Failed to fetch gifts');
    }
  }

  // Get gift settings (Firestore only — avoids callable CORS/us-central1 payload errors)
  async getSettings(): Promise<GiftSettings> {
    try {
      // Direct Firestore query
      const settingsRef = collection(db, this.settingsCollection);
      const querySnapshot = await getDocs(settingsRef);
      
      if (querySnapshot.empty) {
        // Initialize default settings if they don't exist
        const defaultSettings = {
          enabled: true,
          minGifts: 3,
          maxGifts: 7,
          showPopup: true,
          autoAccept: false,
          modalBackgroundImage: '',
          modalBackgroundColor: '#ffffff',
          modalButtonColor: '#9333ea',
          modalButtonTextColor: '#ffffff',
          modalHeading: "You've Got a Gift!",
          modalTagline: 'A special surprise has been added to your cart',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Try to create default settings
        try {
          const { addDoc } = await import('firebase/firestore');
          await addDoc(settingsRef, defaultSettings);
        } catch (err) {
          // Could not create default settings (may need admin access)
          console.warn('Could not create default gift settings:', err);
        }
        
        return defaultSettings;
      }

      const settingsDoc = querySnapshot.docs[0];
      const data = settingsDoc.data();
      
      const settings = {
        enabled: data.enabled !== undefined ? data.enabled : true,
        minGifts: data.minGifts || 3,
        maxGifts: data.maxGifts || 7,
        showPopup: data.showPopup !== undefined ? data.showPopup : true,
        autoAccept: data.autoAccept !== undefined ? data.autoAccept : false,
        modalBackgroundImage: data.modalBackgroundImage || '',
        modalBackgroundColor: data.modalBackgroundColor || '#ffffff',
        modalButtonColor: data.modalButtonColor || '#9333ea',
        modalButtonTextColor: data.modalButtonTextColor || '#ffffff',
        modalHeading: data.modalHeading || "You've Got a Gift!",
        modalTagline: data.modalTagline || 'A special surprise has been added to your cart',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
      
      return settings;
    } catch (error) {
      console.error('❌ Error fetching gift settings:', error);
      // Return safe defaults on any error
      return {
        enabled: true,
        minGifts: 3,
        maxGifts: 7,
        showPopup: true,
        autoAccept: false,
        modalBackgroundImage: '',
        modalBackgroundColor: '#ffffff',
        modalButtonColor: '#9333ea',
        modalButtonTextColor: '#ffffff',
        modalHeading: "You've Got a Gift!",
        modalTagline: 'A special surprise has been added to your cart'
      };
    }
  }

  // Select random gifts — Firestore only (same logic as former Cloud Function; no callable/CORS)
  async selectRandomGifts(): Promise<GiftSelectionResult> {
    try {
      return await this.selectRandomGiftsDirect();
    } catch (e) {
      console.error('❌ selectRandomGifts failed:', e);
      return { gifts: [], shouldShowPopup: false, autoAccept: false };
    }
  }

  // Fallback method to select gifts directly from Firestore
  private async selectRandomGiftsDirect(): Promise<GiftSelectionResult> {
    try {
      const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
      const giftsRef = collection(db, this.giftsCollection);
      
      // Get active gifts
      const activeGiftsQuery = query(giftsRef, where('isActive', '==', true));
      const giftsSnapshot = await getDocs(activeGiftsQuery);
      
      if (giftsSnapshot.empty) {
        return {
          gifts: [],
          shouldShowPopup: false,
          autoAccept: false
        };
      }
      
      const allGifts = giftsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        imageURL: doc.data().imageURL || '',
        description: doc.data().description || '',
        rarity: doc.data().rarity || 'common',
        weight: doc.data().weight || 1
      }));
      
      // Get settings
      const settings = await this.getSettings();
      
      if (!settings.enabled) {
        return {
          gifts: [],
          shouldShowPopup: false,
          autoAccept: false
        };
      }
      
      // Calculate number of gifts to select
      const numGifts = Math.min(
        Math.max(settings.minGifts, Math.floor(Math.random() * (settings.maxGifts - settings.minGifts + 1)) + settings.minGifts),
        allGifts.length
      );
      
      // Weighted random selection
      const selectedGifts = [];
      const availableGifts = [...allGifts];
      
      for (let i = 0; i < numGifts && availableGifts.length > 0; i++) {
        const totalWeight = availableGifts.reduce((sum, gift) => sum + (gift.weight || 1), 0);
        let random = Math.random() * totalWeight;
        
        let selectedIndex = 0;
        for (let j = 0; j < availableGifts.length; j++) {
          random -= (availableGifts[j].weight || 1);
          if (random <= 0) {
            selectedIndex = j;
            break;
          }
        }
        
        selectedGifts.push(availableGifts[selectedIndex]);
        availableGifts.splice(selectedIndex, 1);
      }
      
      return {
        gifts: selectedGifts.map(g => ({
          id: g.id,
          name: g.name,
          imageURL: g.imageURL,
          description: g.description,
          rarity: g.rarity
        })),
        shouldShowPopup: settings.showPopup,
        autoAccept: settings.autoAccept
      };
    } catch (error) {
      console.error('❌ Error in direct gift selection:', error);
      return {
        gifts: [],
        shouldShowPopup: false,
        autoAccept: false
      };
    }
  }

  // Create gift using backend function
  async createGift(giftData: Omit<GiftItem, 'id'> & { weight?: number; isActive?: boolean }): Promise<string> {
    try {
      const createGiftFunction = httpsCallable(giftFunctions, 'createGift');
      const result = await createGiftFunction({
        name: giftData.name,
        imageURL: giftData.imageURL,
        description: giftData.description,
        rarity: giftData.rarity,
        weight: giftData.weight || 1,
        isActive: giftData.isActive !== undefined ? giftData.isActive : true
      });
      
      const data = result.data as any;
      return data.giftId;
    } catch (error: any) {
      console.error('Error creating gift:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      
      // Always fallback to direct Firestore operation if function fails
      // This ensures gifts can be created even if Firebase functions have issues
      try {
        return await this.createGiftDirect(giftData);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error('Failed to create gift');
      }
    }
  }

  // Fallback method to create gift directly in Firestore
  private async createGiftDirect(giftData: Omit<GiftItem, 'id'> & { weight?: number; isActive?: boolean }): Promise<string> {
    try {
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const giftsRef = collection(db, this.giftsCollection);
      
      const docRef = await addDoc(giftsRef, {
        name: giftData.name,
        imageURL: giftData.imageURL,
        description: giftData.description,
        rarity: giftData.rarity,
        weight: giftData.weight || 1,
        isActive: giftData.isActive !== undefined ? giftData.isActive : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating gift directly:', error);
      throw new Error('Failed to create gift');
    }
  }

  // Update gift using backend function
  async updateGift(giftId: string, updates: Partial<GiftItem> & { weight?: number; isActive?: boolean }): Promise<void> {
    try {
      const updateGiftFunction = httpsCallable(giftFunctions, 'updateGift');
      await updateGiftFunction({
        giftId,
        ...updates
      });
    } catch (error: any) {
      console.error('Error updating gift:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      
      // Always fallback to direct Firestore operation if function fails
      try {
        return await this.updateGiftDirect(giftId, updates);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error('Failed to update gift');
      }
    }
  }

  // Fallback method to update gift directly in Firestore
  private async updateGiftDirect(giftId: string, updates: Partial<GiftItem> & { weight?: number; isActive?: boolean }): Promise<void> {
    try {
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const giftRef = doc(db, this.giftsCollection, giftId);
      
      const updateData: any = {
        updatedAt: serverTimestamp()
      };
      
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.imageURL !== undefined) updateData.imageURL = updates.imageURL;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.rarity !== undefined) updateData.rarity = updates.rarity;
      if (updates.weight !== undefined) updateData.weight = updates.weight;
      if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
      
      await updateDoc(giftRef, updateData);
    } catch (error) {
      console.error('Error updating gift directly:', error);
      throw new Error('Failed to update gift');
    }
  }

  // Delete gift using backend function
  async deleteGift(giftId: string): Promise<void> {
    try {
      const deleteGiftFunction = httpsCallable(giftFunctions, 'deleteGift');
      await deleteGiftFunction({ giftId });
    } catch (error: any) {
      console.error('Error deleting gift:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      
      // Always fallback to direct Firestore operation if function fails
      try {
        return await this.deleteGiftDirect(giftId);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error('Failed to delete gift');
      }
    }
  }

  // Fallback method to delete gift directly from Firestore
  private async deleteGiftDirect(giftId: string): Promise<void> {
    try {
      const { doc, deleteDoc } = await import('firebase/firestore');
      const giftRef = doc(db, this.giftsCollection, giftId);
      await deleteDoc(giftRef);
    } catch (error) {
      console.error('Error deleting gift directly:', error);
      throw new Error('Failed to delete gift');
    }
  }

  // Update gift settings using backend function
  async updateSettings(settings: Partial<GiftSettings>): Promise<void> {
    try {
      const updateGiftSettingsFunction = httpsCallable(giftFunctions, 'updateGiftSettings');
      await updateGiftSettingsFunction(settings);
    } catch (error: any) {
      console.error('Error updating gift settings:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      
      // Always fallback to direct Firestore operation if function fails
      try {
        return await this.updateSettingsDirect(settings);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error('Failed to update gift settings');
      }
    }
  }

  // Fallback method to update settings directly in Firestore
  private async updateSettingsDirect(settings: Partial<GiftSettings>): Promise<void> {
    try {
      const { collection, getDocs, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const settingsRef = collection(db, this.settingsCollection);
      const snapshot = await getDocs(settingsRef);
      
      const updateData: any = {
        ...settings,
        updatedAt: serverTimestamp()
      };
      
      if (snapshot.empty) {
        // Create new settings document
        updateData.createdAt = serverTimestamp();
        const newDocRef = doc(settingsRef);
        await setDoc(newDocRef, updateData);
      } else {
        // Update existing settings document
        const existingDoc = snapshot.docs[0];
        await setDoc(existingDoc.ref, updateData, { merge: true });
      }
    } catch (error) {
      console.error('Error updating settings directly:', error);
      throw new Error('Failed to update gift settings');
    }
  }
}

const giftService = new GiftService();
export default giftService;
